import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle } from "lucide-react";
import { FC } from "react";
import { ErrorContent } from "@/components/IAndI/SummaryMetrics/ErrorContent/ErrorContent";
import { NotFoundContent } from "@/components/IAndI/SummaryMetrics/NotFoundContent";
import { BlockageStatusBadge } from "@/components/badges/BlockageStatusBadge";

const DISPLAY_COUNT = 5;

const CurrentlyBlockedSkeleton: FC = () => (
	<div className="flex flex-col items-center gap-4">
		<Skeleton className="h-12 w-64 rounded-md bg-blue-50 mt-2" />
		<Skeleton className="h-4 w-64 rounded-md bg-blue-50" />
		<div className="w-full flex flex-col gap-2">
			{Array.from({ length: DISPLAY_COUNT }).map((_, index) => (
				<Skeleton key={index} className="h-5 rounded-md bg-blue-50 w-full" />
			))}
		</div>
	</div>
);

function getBlockedStructures(blockages: Dryp.Blockage[], n: number): Dryp.Blockage[] {
	return [...blockages]
		.filter(b => b.status.status === "Blocked" || b.status.status === "Warning" || b.status.status === "Full")
		.sort((a, b) => (b.maxValue ?? 0) - (a.maxValue ?? 0))
		.slice(0, n);
}

interface CurrentlyBlockedCardProps {
	loading: boolean;
	loaded: boolean;
	error: ApiError | null;
	blockages: Dryp.Blockage[];
}

export const CurrentlyBlockedCard: FC<CurrentlyBlockedCardProps> = ({ loading, loaded, error, blockages }) => {
	const blockedStructures = getBlockedStructures(blockages, DISPLAY_COUNT);
	const isNoData = loaded && !error && blockages.length === 0;
	const noneBlocked = loaded && !error && blockages.length > 0 && blockedStructures.length === 0;

	return (
		<Card className="shadow-sm border border-muted rounded-xl flex flex-col h-full drop-shadow-sm">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold flex items-center gap-2">
					<AlertTriangle className="h-4 w-4 text-amber-500" />
					Structures to Watch
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-1 flex flex-col justify-center">
				{loading ? (
					<CurrentlyBlockedSkeleton />
				) : isNoData ? (
					<NotFoundContent message="No blockage data available" />
				) : error ? (
					<ErrorContent
						error={error}
						notFoundMessage="No blockage data available"
						message="Unable to load blockage data"
					/>
				) : noneBlocked ? (
					<NotFoundContent message="All structures look normal" />
				) : (
					<div className="space-y-1.5 text-xs">
						{blockedStructures.map((b, index) => (
							<div key={b.id} className="flex items-center justify-between border-b pb-1 gap-2">
								<span className="font-medium text-foreground truncate">
									{index + 1}. {b.structureName}
								</span>
								<div className="flex items-center gap-2 shrink-0">
									{b.maxValue != null && (
										<span className="text-muted-foreground font-mono tabular-nums">
											{b.maxValue.toFixed(1)}%
										</span>
									)}
									<BlockageStatusBadge status={b.status} mode="dot" showLabel={false} />
								</div>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
};
